import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiClientError } from "../api.js";
import type {
  Client,
  PromptTemplate,
  TemplateCategory,
  TemplateScope,
} from "../types.js";

export function TemplatesScreen() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [scope, setScope] = useState("all");
  const [status, setStatus] = useState("all");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    const query = new URLSearchParams({
      search,
      category,
      scope,
      status,
      page: "1",
      pageSize: "50",
      sort: "updated_at",
      order: "desc",
    });
    try {
      const result = await api.listTemplates(query);
      setTemplates(result.items);
      setTotal(result.total);
      setError("");
    } catch {
      setError("Не удалось загрузить шаблоны.");
    }
  };
  useEffect(() => {
    const timeout = window.setTimeout(() => void load(), 150);
    return () => window.clearTimeout(timeout);
  }, [search, category, scope, status]);
  useEffect(() => {
    void api
      .listClients(new URLSearchParams({ status: "active", pageSize: "100" }))
      .then((result) => setClients(result.items));
  }, []);
  return (
    <section className="panel release-table-panel">
      <div className="page-heading">
        <div>
          <h1>Шаблоны</h1>
          <p>Шаблоны генерации изображений · {total} записей</p>
        </div>
        <button
          className="primary compact-button"
          onClick={() => setCreating(true)}
        >
          ＋ Новый шаблон
        </button>
      </div>
      <div className="table-toolbar template-toolbar">
        <label className="search-field">
          ⌕
          <input
            aria-label="Поиск шаблонов"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Название или описание"
          />
        </label>
        <select
          aria-label="Категория шаблона"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          <option value="all">Все категории</option>
          {categories.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label="Доступность шаблона"
          value={scope}
          onChange={(event) => setScope(event.target.value)}
        >
          <option value="all">Любая доступность</option>
          <option value="global">Общий</option>
          <option value="client">Для клиента</option>
        </select>
        <select
          aria-label="Статус шаблона"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">Все статусы</option>
          <option value="draft">Черновик</option>
          <option value="published">Опубликованные</option>
          <option value="archived">Архивные</option>
        </select>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      <div className="release-table template-table" role="table">
        <div className="release-row release-head" role="row">
          <span>Название</span>
          <span>Категория</span>
          <span>Доступность</span>
          <span>Клиент</span>
          <span>Статус</span>
          <span>Версия</span>
          <span>Обновлён</span>
          <span />
        </div>
        {templates.map((template) => (
          <div className="release-row" role="row" key={template.id}>
            <span>
              <Link to={`/templates/${template.id}`}>
                <strong>{template.name}</strong>
              </Link>
            </span>
            <span>{categoryLabel(template.category)}</span>
            <span>{template.scope === "global" ? "Общий" : "Для клиента"}</span>
            <span>{template.clientName ?? "—"}</span>
            <span>
              <b className={`status-pill ${template.status}`}>
                {template.status === "published"
                  ? "Опубликован"
                  : template.status === "draft"
                    ? "Черновик"
                    : "В архиве"}
              </b>
              {template.hasDraft && template.status === "published" ? (
                <small>есть черновик</small>
              ) : null}
            </span>
            <span>
              {template.currentVersionNumber
                ? `v${template.currentVersionNumber}`
                : "—"}
            </span>
            <span>{formatDate(template.updatedAt)}</span>
            <span className="row-actions">
              <Link
                aria-label={`Открыть ${template.name}`}
                to={`/templates/${template.id}`}
              >
                →
              </Link>
            </span>
          </div>
        ))}
        {!templates.length ? (
          <div className="table-message">Шаблоны не найдены</div>
        ) : null}
      </div>
      {creating ? (
        <CreateTemplateDialog
          clients={clients}
          onClose={() => setCreating(false)}
        />
      ) : null}
    </section>
  );
}

function CreateTemplateDialog({
  clients,
  onClose,
}: {
  clients: Client[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<TemplateCategory>("product");
  const [scope, setScope] = useState<TemplateScope>("global");
  const [clientId, setClientId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const created = await api.createTemplate({
        name,
        description,
        category,
        scope,
        ...(scope === "client" ? { clientId } : {}),
      });
      void navigate(`/templates/${created.id}`);
    } catch (reason) {
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : "Не удалось создать шаблон.",
      );
      setSaving(false);
    }
  };
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog compact-dialog"
        aria-label="Новый шаблон"
        onSubmit={(event) => void submit(event)}
      >
        <div className="dialog-heading">
          <div>
            <h2>Создать шаблон</h2>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <label>
          Имя шаблона
          <input
            aria-label="Имя шаблона"
            required
            maxLength={160}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Студийное фото товара"
          />
        </label>
        <label>
          Описание
          <textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <div className="form-grid">
          <label>
            Категория
            <select
              value={category}
              onChange={(event) =>
                setCategory(event.target.value as TemplateCategory)
              }
            >
              {categories.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Доступность
            <select
              aria-label="Scope нового шаблона"
              value={scope}
              onChange={(event) =>
                setScope(event.target.value as TemplateScope)
              }
            >
              <option value="global">Общий</option>
              <option value="client">Для клиента</option>
            </select>
          </label>
        </div>
        {scope === "client" ? (
          <label>
            Клиент
            <select
              aria-label="Клиент шаблона"
              required
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            >
              <option value="">Выберите клиента</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary" disabled={saving}>
          {saving ? "Создание…" : "Создать и открыть"}
        </button>
      </form>
    </div>
  );
}

export const categories: Array<[TemplateCategory, string]> = [
  ["product", "Продукция"],
  ["article", "Статья"],
  ["social", "Соцсети"],
  ["banner", "Баннер"],
  ["advertising", "Реклама"],
  ["process", "Процесс"],
  ["custom", "Пользовательская"],
];
function categoryLabel(value: TemplateCategory) {
  return categories.find(([category]) => category === value)?.[1] ?? value;
}
function formatDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(new Date(value));
}
